import { Card, Metadata } from "@dropbox/dash-component-library";
import { LabelGroup } from "@dropbox/dig-components/label_group";
import { Text } from "@dropbox/dig-components/typography";
import { Split } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { DashStackIcon } from "@mirage/mosaics/DCLWrappers/DashStackIcon";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import { DashTruncateWithTooltip } from "@mirage/shared/util/DashTruncateWithTooltip";
import { getTimeAgoString } from "@mirage/shared/util/time";
import i18n, { meticulousRedactedStringClass } from "@mirage/translations";
import styles from "./AllStacksList.module.css";

import type {
  IconLayoutProps,
  LabelGroupProps,
} from "@dropbox/dash-component-library";
import type { WorkingSetCardData } from "@mirage/working-set";

export const AllStacksListItem = ({
  workingSetCardData,
}: {
  workingSetCardData: WorkingSetCardData;
}) => {
  const isMobileSize = useIsMobileSize();
  const onClick = workingSetCardData.onClickCard;

  return (
    // TODO (yong):
    // This should be converted to use ContentRow, with the facepile as an
    // accessory along with the action items
    <Card.Module breakout="xsmall" isLink>
      <div className={styles.allStacksListItem} data-testid="stackListItem">
        <Split
          alignY="center"
          className={styles.fullWidth}
          direction={isMobileSize ? "vertical" : "horizontal"}
          gap={"Micro Large"}
        >
          <Split.Item marginRight="auto">
            <Card.OverlayLink
              onClick={onClick}
              href={`/stacks/${workingSetCardData.id}`}
            >
              <StacksMetadata workingSetCardData={workingSetCardData} />
            </Card.OverlayLink>
          </Split.Item>
          <Split.Item aria-label={workingSetCardData.accessLevel} tabIndex={0}>
            {workingSetCardData.memberCount > 1 &&
            !workingSetCardData.isCompanyPublishedStack &&
            !workingSetCardData.isPublicStack ? (
              workingSetCardData.getMembersComponent("small")
            ) : (
              <Text color="subtle" className={styles.accessIcon}>
                <UIIcon src={workingSetCardData.accessLevelIcon} />
                {workingSetCardData.accessLevel}
              </Text>
            )}
          </Split.Item>
          <Split.Item>
            <div className={styles.actionMenuComponent}>
              {workingSetCardData.actionMenuComponent}
            </div>
          </Split.Item>
        </Split>
      </div>
    </Card.Module>
  );
};

export const StacksMetadata = ({
  workingSetCardData,
  stackIconAccessorySize,
  gap,
  isTitleBold,
}: {
  workingSetCardData: WorkingSetCardData;
  stackIconAccessorySize?: IconLayoutProps["size"];
  gap?: LabelGroupProps["accessoryGap"];
  isTitleBold?: boolean;
}) => {
  // first check for lastModifiedMs and fallback to maxHlcMs
  const updatedAtMs =
    (workingSetCardData.lastModifiedMs || workingSetCardData.maxHlcMs) ?? 0;

  return (
    <LabelGroup
      accessoryGap={gap || "Micro XLarge"}
      verticalAlignment="center"
      withLabel={
        <Text
          size="medium"
          isBold={isTitleBold}
          className={meticulousRedactedStringClass}
        >
          <DashTruncateWithTooltip
            lines={3}
            tooltipProps={{
              title: workingSetCardData.title,
            }}
          >
            {workingSetCardData.title}
          </DashTruncateWithTooltip>
        </Text>
      }
      withMeta={
        <Metadata>
          <Metadata.Item>
            <Metadata.Label>
              {i18n.t("num_items", {
                count: workingSetCardData.links.length,
              })}
            </Metadata.Label>
          </Metadata.Item>
          {Boolean(updatedAtMs) && (
            <Metadata.Item>
              <Metadata.Label>
                {i18n.t("updated_ago", {
                  timeAgo: getTimeAgoString(updatedAtMs),
                })}
              </Metadata.Label>
            </Metadata.Item>
          )}
        </Metadata>
      }
      withStartAccessory={
        <DashStackIcon
          size={stackIconAccessorySize || "xlarge"}
          colorIndex={workingSetCardData.colorIndex}
          emoji={workingSetCardData.emoji}
          customColor={workingSetCardData.customColor}
        />
      }
    />
  );
};
